import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import EditIcon from "@mui/icons-material/Edit";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useDeleteSelectedProjectsMutation,
  useDeleteSingleProjectsMutation,
  useGetAllProjectsQuery,
} from "../../redux/slices/projectApiSlice";
import { H1Header } from "../../shared/components/others";
import { DeleteIcon } from "../../shared/components/others/Icons";
import { trackPlatformUsed } from "../../shared/utils/trackers";

const ProjectList = () => {
  const { t } = useTranslation();

  const { data: projects, refetch } = useGetAllProjectsQuery();
  const [deleteProject, { isLoading: deleteLoading }] =
    useDeleteSingleProjectsMutation();
  const [deleteSelectedProject, { isLoading: deleteSelectedLoading }] =
    useDeleteSelectedProjectsMutation();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deletionType, setDeletionType] = useState("single");
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");

  const projectsList = projects?.data ? projects?.data : [];

  const isAllSelected =
    projectsList &&
    projectsList.length > 0 &&
    projectsList.every((project) => selectedProjects.has(project.id));

  const handleDeleteModal = (deleteType, open = true, id = null) => {
    setDeletionType(deleteType);
    setSelectedId(id);
    if (open) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleDeleteSelectedProject = async () => {
    try {
      const res = await deleteSelectedProject(
        Array.from(selectedProjects)
      ).unwrap();
      setOpen(false);
      refetch();
      toast.success(
        t(
          "deleted-selected-projects",
          "Les projets sélectionnés ont bien été supprimés"
        )
      );
    } catch (error) {
      toast.error(
        t(
          "error-deleting-selected-projects",
          "Une erreur a eu lieu lors de la suppression des projets"
        )
      );
    }
  };

  const handleDeleteProject = async (id, modal = false) => {
    try {
      const res = await deleteProject({ id }).unwrap();
      if (modal) {
        setOpen(false);
      }
      refetch();
      toast.success(
        t("deleted-project", "Le projet sélectionné a bien été supprimé")
      );
    } catch (error) {
      toast.error(
        t(
          "error-deleting-project",
          "Une erreur a eu lieu lors de la suppression du projet"
        )
      );
    }
  };

  // CHECKBOX AND BULK ACTIONS
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allProjectIds = projectsList.map((post) => post.id);
      setSelectedProjects(new Set(allProjectIds));
    } else {
      setSelectedProjects(new Set());
    }
  };

  const handleCheckboxChange = (projectId) => {
    setSelectedProjects((prevSelectedProjects) => {
      const newSelectedProjects = new Set(prevSelectedProjects);
      if (newSelectedProjects.has(projectId)) {
        newSelectedProjects.delete(projectId);
      } else {
        newSelectedProjects.add(projectId);
      }
      return newSelectedProjects;
    });
  };

  const handleApplyAction = async () => {
    if (bulkAction === "delete") {
      handleDeleteModal("selected", true, null);
    }
  };

  return (
    <div>
      <Helmet>
        <title>{t("projects", "Projets")} | BotBlogR</title>
        <meta
          name="description"
          content="Vision générale de votre compte et de vos activités."
        />
      </Helmet>

      <div className="px-2 pl-6 pr-6">
        <div className="flex items-center justify-between py-3">
          <H1Header title={t("My-projects", "Mes projets")} />
          <button
            className="px-5 py-[6px] hover:bg-blue-200 *: bg-blue-100 text-white rounded font-light"
            onClick={() => {
              trackPlatformUsed("project", "create_new");
              navigate("/projects/select-platform");
            }}
          >
            {t("create-new-project", "Créer un nouveau projet")}
          </button>
        </div>
      </div>

      <div className="bg-gray-100 p-4">
        <div className="mx-auto bg-white shadow-md rounded-md overflow-hidden">
          <div className="p-4">
            <table className="w-full text-sm text-left rtl:text-right text-gray-500">
              <thead className="text-xs text-white uppercase bg-blue-100">
                <tr>
                  {["", t("Nom", "Nom"), t("Action", "Actions")].map(
                    (heading, index) => (
                      <th
                        key={index}
                        className={`[&:nth-child(2)]:w-[80%] px-6 py-3 ${
                          index === 0
                            ? "rounded-s-lg"
                            : index === 2
                            ? "rounded-e-lg"
                            : ""
                        } text-base col`}
                      >
                        {index === 0 ? (
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                          />
                        ) : (
                          heading
                        )}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {projectsList?.map((project) => (
                  <tr
                    key={project.id}
                    className="odd:bg-white even:bg-black/10"
                  >
                    <td
                      scope="col"
                      className="px-6 py-4 text-sm font-medium text-gray-900 break-words"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProjects.has(project.id)}
                        onChange={() => handleCheckboxChange(project.id)}
                      />
                    </td>

                    <td
                      scope="col"
                      className="px-6 py-4 text-sm font-medium text-gray-900 break-words min-w-[60%]"
                    >
                      <div className="inline-flex gap-2">
                        <img
                          src={project.favicon}
                          alt={project.name}
                          className="w-10 h-10"
                        />
                        <div>
                          {project.name ? project.name : "(Nom indisponible)"}
                          <p
                            className="px-0 py-[4px] text-black min-w-[128px]
                          font-medium text-xs"
                          >
                            {project.url ? project.url : "(Url indisponible)"}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td
                      scope="col"
                      className="px-6 py-4 font-medium text-gray-900 flex gap-6 w-48"
                    >
                      <button
                        className="px-5 py-[6px] hover:bg-blue-200 bg-blue-100 text-white rounded font-light"
                        onClick={() => navigate(`/project/${project.id}`)}
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="px-5 py-[6px] hover:bg-[#f44336] bg-[#ff574b] text-white rounded font-light"
                        onClick={() =>
                          handleDeleteModal("single", true, project.id)
                        }
                      >
                        <DeleteIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex space-x-4 mt-4">
              <select
                className="border rounded p-2 bg-white"
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
              >
                <option value="">{t("bulk-action", "Actions groupées")}</option>
                <option value="delete">{t("delete", "Supprimer")}</option>
              </select>
              <button
                className="px-5 py-[6px] hover:bg-blue-200 *: bg-blue-100 text-white disabled:bg-slate-200 disabled:text-slate-400 rounded font-light"
                onClick={handleApplyAction}
                disabled={selectedProjects.size === 0}
              >
                {t("apply", "Appliquer")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => handleDeleteModal("single", false, null)}
        className="relative z-[999]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-[999] w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-2xl sm:p-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  <div className="flex items-center gap-3">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-error/30 sm:mx-0 sm:h-10 sm:w-10">
                      <PriorityHighIcon
                        aria-hidden="true"
                        className="h-8 w-8 text-error"
                      />
                    </div>
                    <p className="text-base font-semibold leading-6 text-black/40">
                      {deletionType === "single"
                        ? t("delete-project", "Supprimer le projet")
                        : t(
                            "delete-selected-projects",
                            "Supprimer les projets sélectionnés"
                          )}
                    </p>
                  </div>
                </DialogTitle>
                <p className="text-base text-black text-left mt-5">
                  {deletionType === "single"
                    ? "Êtes-vous sûr de vouloir supprimer votre projet ?"
                    : "Êtes-vous sûr de vouloir supprimer ces projets ?"}
                </p>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={
                      deletionType === "single"
                        ? () => handleDeleteProject(selectedId, true)
                        : () => handleDeleteSelectedProject()
                    }
                    className="inline-flex w-full justify-center rounded-md bg-error px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  >
                    Supprimer
                  </button>
                  <button
                    type="button"
                    data-autofocus
                    onClick={() => handleDeleteModal("single", false, null)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProjectList;
